import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ExternalLink, Pencil, Trash2, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { STATUS_META } from "./status";

function daysLabel(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  if (d < 0) return `${-d}d ago`;
  if (d === 0) return "today";
  return `in ${d}d`;
}

export function ApplicationCard({ app, statuses, onEdit, onPatch, onDelete }) {
  const [open, setOpen] = useState(false);
  const [newMat, setNewMat] = useState("");
  const done = app.materials.filter(m => m.done).length;
  const meta = STATUS_META[app.status] || STATUS_META.researching;
  const overdue = app.deadline && new Date(app.deadline) < new Date() && !["submitted", "interview", "offer", "accepted", "rejected", "withdrawn"].includes(app.status);

  const toggleMat = (i) => onPatch({ materials: app.materials.map((m, j) => (j === i ? { ...m, done: !m.done } : m)) });
  const addMat = () => { if (!newMat.trim()) return; onPatch({ materials: [...app.materials, { name: newMat.trim(), done: false }] }); setNewMat(""); };
  const removeMat = (i) => onPatch({ materials: app.materials.filter((_, j) => j !== i) });

  return (
    <div className="aria-card p-4" data-testid={`application-card-${app.application_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`aria-pill text-[10px] uppercase tracking-wide ${meta.cls}`}>{meta.label}</span>
            <span className="aria-pill text-[10px] uppercase tracking-wide">{app.type}</span>
            <span className="font-medium text-sm truncate" data-testid="application-title">{app.title}</span>
            {app.institution && <span className="text-sm text-[var(--aria-text-muted)] truncate">— {app.institution}</span>}
          </div>
          <div className="text-xs text-[var(--aria-text-muted)] mt-1.5 flex gap-3 flex-wrap">
            <Link to="/app/chat" state={{ application_id: app.application_id, prompt: "Use the framework from my shared project to prepare me for this application." }} data-testid={`application-shared-context-${app.application_id}`} className="text-[var(--aria-primary)] hover:underline">Ask ARIA with shared context</Link>
            {app.deadline && <span className={overdue ? "text-red-400" : ""} data-testid="application-deadline">Due {new Date(app.deadline).toLocaleDateString()} · {daysLabel(app.deadline)}</span>}
            {app.interview_date && <span className="text-[var(--aria-accent)]">Interview {new Date(app.interview_date).toLocaleString()}</span>}
            <span data-testid="application-materials-progress">Materials {done}/{app.materials.length}</span>
            {app.link && <a href={app.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[var(--aria-primary)]"><ExternalLink size={10} /> posting</a>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select value={app.status} onChange={e => onPatch({ status: e.target.value })} data-testid="application-status-quick"
            className="px-2 py-1 text-xs rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none">
            {statuses.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
          </select>
          <button onClick={onEdit} data-testid="application-edit" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><Pencil size={14} /></button>
          <button onClick={onDelete} data-testid="application-delete" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-danger)]"><Trash2 size={14} /></button>
          <button onClick={() => setOpen(!open)} data-testid="application-expand" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
        </div>
      </div>

      <div className="mt-3 h-1 rounded-full bg-[var(--aria-surface-2)] overflow-hidden">
        <div className="h-full bg-[var(--aria-primary)] transition-all" style={{ width: `${app.materials.length ? (done / app.materials.length) * 100 : 0}%` }} />
      </div>

      {open && (
        <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="application-details">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--aria-text-muted)] mb-2">Materials</div>
            <ul className="space-y-1.5">
              {app.materials.map((m, i) => (
                <li key={`${m.name}-${i}`} className="flex items-center justify-between gap-2 text-sm group">
                  <button onClick={() => toggleMat(i)} data-testid={`material-toggle-${i}`} className="flex items-center gap-2 text-left">
                    {m.done ? <CheckCircle2 size={15} className="text-emerald-400" /> : <Circle size={15} className="text-[var(--aria-text-muted)]" />}
                    <span className={m.done ? "line-through text-[var(--aria-text-muted)]" : ""}>{m.name}</span>
                  </button>
                  <button onClick={() => removeMat(i)} className="opacity-0 group-hover:opacity-100 text-[var(--aria-text-muted)] hover:text-[var(--aria-danger)]"><Trash2 size={12} /></button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 mt-2">
              <input value={newMat} onChange={e => setNewMat(e.target.value)} onKeyDown={e => e.key === "Enter" && addMat()} placeholder="Add material…" data-testid="material-new-input"
                className="flex-1 px-2 py-1 text-xs rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none" />
              <button onClick={addMat} data-testid="material-add-button" className="px-2 rounded-md border border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><Plus size={12} /></button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--aria-text-muted)] mb-2">Notes & contact</div>
            {app.contact && <div className="text-sm mb-1"><span className="text-[var(--aria-text-muted)]">Contact:</span> {app.contact}</div>}
            <div className="text-sm whitespace-pre-wrap text-[var(--aria-text-muted)]">{app.notes || "No notes yet."}</div>
          </div>
        </div>
      )}
    </div>
  );
}
